import React, { useState, useMemo, useRef } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import styled from "@emotion/styled";
import {
  Box,
  Button,
  Flex,
  Icon,
  Text,
  VStack,
  HStack,
  Badge,
  Select,
  Spinner,
  Switch,
  Table,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  Avatar,
  useToast,
  useColorModeValue,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  InfoIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { MdRefresh, MdLock } from "react-icons/md";
import { motion } from "framer-motion";
import useAxios from "axios-hooks";
import { useRecoilValue } from "recoil";
import Card from "components/card/Card";
import { loggedInUserState } from "atoms/loggedInUserState";
import { onlyUAdmins } from "utils/accessLevels";

const GlassMorphismCard = styled(Card)`
  background: rgba(255, 255, 255, 0.6) !important;
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  transition: all 0.3s ease;
  overflow: hidden;

  &:hover {
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  }
`;

const MotionTr = motion(Tr);

const PaginationButton = ({ children, isActive, isDisabled, onClick }) => (
  <Button
    size="sm"
    variant={isActive ? "solid" : "outline"}
    colorScheme={isActive ? "orange" : "gray"}
    isDisabled={isDisabled}
    onClick={onClick}
    minW="40px"
    h="40px"
    borderRadius="md"
    _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
    transition="all 0.2s"
  >
    {children}
  </Button>
);

const stationColumns = [
  { Header: "S.No" },
  { Header: "Station Name" },
  { Header: "Station ID" },
  { Header: "Location ID" },
  { Header: "OCPI Enabled" },
];

const OcpiStationsTable = ({ stations, refetchStations, stListLoading }) => {
  const toast = useToast();
  const loggedInUser = useRecoilValue(loggedInUserState);
  const canEdit = onlyUAdmins.includes(loggedInUser?.role);
  const [stationSearch, setStationSearch] = useState("");
  const [ocpiFilter, setOcpiFilter] = useState("all");
  const [updatingStationId, setUpdatingStationId] = useState(null);
  const [confirmState, setConfirmState] = useState({
    open: false,
    station: null,
    nextValue: false,
  });
  const cancelRef = useRef();

  const [, executeStationUpdate] = useAxios({}, { manual: true });

  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const theadBgColor = useColorModeValue("gray.50", "gray.800");

  const columns = useMemo(() => stationColumns, []);
  const data = useMemo(() => {
    if (ocpiFilter === "enabled") {
      return stations.filter((station) => station.IsOcpiEnabled);
    }
    if (ocpiFilter === "disabled") {
      return stations.filter((station) => !station.IsOcpiEnabled);
    }
    return stations;
  }, [stations, ocpiFilter]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
      globalFilter: (rows, ids, query) => {
        if (!query || query.trim() === "") return rows;
        const q = query.toLowerCase().trim();
        return rows.filter((row) => {
          const o = row.original;
          return (
            (o.Name || "").toLowerCase().includes(q) ||
            (o.StationId || "").toLowerCase().includes(q) ||
            String(o.Id ?? "").includes(q) ||
            String(o.LocationId ?? "").includes(q)
          );
        });
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setStationSearch(v);
    tableInstance.setGlobalFilter(v);
  };

  const closeConfirm = () =>
    setConfirmState({ open: false, station: null, nextValue: false });

  const applyStationUpdate = async (station, nextValue) => {
    setUpdatingStationId(station.Id);
    try {
      await executeStationUpdate({
        method: "PATCH",
        url: `/api/stations/update/${station.Id}`,
        data: { IsOcpiEnabled: nextValue },
      });
      toast({
        title: `OCPI ${nextValue ? "enabled" : "disabled"} for station ${
          station.StationId || station.Id
        }`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      refetchStations();
    } catch (err) {
      toast({
        title: "Error updating station OCPI setting",
        description:
          err?.response?.data?.error || err?.message || "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdatingStationId(null);
    }
  };

  const handleToggle = (station, nextValue) => {
    if (station.IsOcpiEnabled && !nextValue) {
      setConfirmState({ open: true, station, nextValue });
      return;
    }
    applyStationUpdate(station, nextValue);
  };

  const handleConfirm = async () => {
    const { station, nextValue } = confirmState;
    closeConfirm();
    if (station) {
      await applyStationUpdate(station, nextValue);
    }
  };

  const renderPagination = () => {
    const {
      page,
      state: { pageIndex, pageSize },
      canPreviousPage,
      canNextPage,
      pageCount,
      gotoPage,
      nextPage,
      previousPage,
      setPageSize: setPS,
    } = tableInstance;

    return (
      <Flex
        justify="space-between"
        align="center"
        px={6}
        py={4}
        borderTop="1px solid"
        borderColor={borderColor}
        flexWrap="wrap"
        gap={3}
      >
        <HStack spacing={3}>
          <Select
            size="sm"
            value={pageSize}
            onChange={(e) => setPS(Number(e.target.value))}
            borderRadius="md"
            w="120px"
          >
            {[5, 10, 20, 30, 50].map((size) => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </Select>
          <Text fontSize="sm" color="gray.500">
            Showing {page.length} of {data.length} results
          </Text>
        </HStack>

        <HStack spacing={2}>
          <PaginationButton isDisabled={!canPreviousPage} onClick={() => gotoPage(0)}>
            <ChevronLeftIcon boxSize={4} mr={1} />
            First
          </PaginationButton>
          <PaginationButton isDisabled={!canPreviousPage} onClick={previousPage}>
            <ChevronLeftIcon boxSize={4} />
          </PaginationButton>

          {pageCount <= 5 ? (
            Array.from({ length: pageCount }, (_, i) => (
              <PaginationButton key={i} isActive={i === pageIndex} onClick={() => gotoPage(i)}>
                {i + 1}
              </PaginationButton>
            ))
          ) : (
            <>
              {pageIndex > 1 && (
                <PaginationButton onClick={() => gotoPage(0)}>1</PaginationButton>
              )}
              {pageIndex > 2 && <Text color="gray.400">...</Text>}
              {pageIndex > 0 && (
                <PaginationButton onClick={() => gotoPage(pageIndex - 1)}>
                  {pageIndex}
                </PaginationButton>
              )}
              <PaginationButton isActive>{pageIndex + 1}</PaginationButton>
              {pageIndex < pageCount - 1 && (
                <PaginationButton onClick={() => gotoPage(pageIndex + 1)}>
                  {pageIndex + 2}
                </PaginationButton>
              )}
              {pageIndex < pageCount - 3 && <Text color="gray.400">...</Text>}
              {pageIndex < pageCount - 2 && (
                <PaginationButton onClick={() => gotoPage(pageCount - 1)}>
                  {pageCount}
                </PaginationButton>
              )}
            </>
          )}

          <PaginationButton isDisabled={!canNextPage} onClick={nextPage}>
            <ChevronRightIcon boxSize={4} />
          </PaginationButton>
          <PaginationButton isDisabled={!canNextPage} onClick={() => gotoPage(pageCount - 1)}>
            Last
            <ChevronRightIcon boxSize={4} ml={1} />
          </PaginationButton>
        </HStack>
      </Flex>
    );
  };

  return (
    <GlassMorphismCard>
      <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          gap={4}
        >
          <VStack align="start" spacing={2} width={{ base: "100%", md: "auto" }}>
            <Text fontSize="xs" fontWeight="medium" color="gray.500">
              Search Stations
            </Text>
            <HStack spacing={10}>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search by station name, ID, location..."
                  value={stationSearch}
                  onChange={handleSearchChange}
                  borderRadius="md"
                  width="350px"
                  _focus={{
                    borderColor: "#f57d36",
                    boxShadow: "0 0 0 1px #f57d36",
                  }}
                />
              </InputGroup>
              <Select
                size="sm"
                value={ocpiFilter}
                onChange={(e) => setOcpiFilter(e.target.value)}
                borderRadius="md"
                w="170px"
              >
                <option value="all">All OCPI statuses</option>
                <option value="enabled">OCPI Enabled</option>
                <option value="disabled">OCPI Disabled</option>
              </Select>
              <Button
                leftIcon={<Icon as={MdRefresh} />}
                size="sm"
                borderRadius="md"
                variant="ghost"
                color="#f57d36"
                onClick={() => refetchStations()}
                isLoading={stListLoading}
                loadingText="Refreshing..."
                aria-label="Refresh stations"
                fontWeight="500"
              >
                Refresh
              </Button>
            </HStack>
            <Text fontSize="xs" color="gray.500" mt={1} ml={1}>
              <Icon as={InfoIcon} boxSize={3} mr={1} />
              Only stations whose location already has OCPI roaming enabled
              are shown here. Enable roaming at the location level first if a
              station is missing.
            </Text>
          </VStack>

          <Flex align="center" justify="flex-end">
            <Text fontSize="xl" fontWeight="bold" color={textColor}>
              Stations
            </Text>
            <Badge ml={2} colorScheme="orange" borderRadius="full" px={2}>
              {stations.length} total
            </Badge>
          </Flex>
        </Flex>
      </Box>

      {stListLoading ? (
        <Flex justify="center" py={10}>
          <Spinner color="#f57d36" size="lg" />
        </Flex>
      ) : data.length === 0 ? (
        <Flex justify="center" py={10}>
          <Text color="gray.400" fontSize="sm">
            {stations.length === 0
              ? "No stations found."
              : "No stations match the selected OCPI status."}
          </Text>
        </Flex>
      ) : (
        <>
          <TableContainer>
            <Table {...tableInstance.getTableProps()} variant="simple" colorScheme="gray">
              <Thead bg={theadBgColor}>
                {tableInstance.headerGroups.map((hg, hgIdx) => (
                  <Tr {...hg.getHeaderGroupProps()} key={hgIdx}>
                    {hg.headers.map((column, colIdx) => (
                      <Th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        key={colIdx}
                        py={4}
                        px={6}
                        borderColor={borderColor}
                        textTransform="uppercase"
                        fontSize="xs"
                        fontWeight="bold"
                        letterSpacing="wider"
                        color="gray.500"
                        textAlign="center"
                        cursor="pointer"
                        _hover={{ color: "#f57d36" }}
                        transition="all 0.2s"
                      >
                        <Flex justify="center" align="center" gap={1}>
                          {column.render("Header")}
                          <Box>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <Icon as={FaSortDown} />
                              ) : (
                                <Icon as={FaSortUp} />
                              )
                            ) : (
                              <Icon as={FaSort} opacity={0.3} />
                            )}
                          </Box>
                        </Flex>
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody {...tableInstance.getTableBodyProps()}>
                {tableInstance.page.map((row, rowIdx) => {
                  tableInstance.prepareRow(row);
                  const station = row.original;
                  return (
                    <MotionTr
                      {...row.getRowProps()}
                      key={row.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: rowIdx * 0.03 }}
                      _hover={{ bg: hoverBg }}
                    >
                      {row.cells.map((cell, cellIdx) => {
                        let cellData;
                        if (cell.column.Header === "S.No") {
                          cellData = (
                            <Flex align="center" justify="center">
                              <Text color={textColor} fontSize="sm" fontWeight="600">
                                {tableInstance.state.pageIndex * tableInstance.state.pageSize + rowIdx + 1}
                              </Text>
                            </Flex>
                          );
                        } else if (cell.column.Header === "Station Name") {
                          const initials = (station.Name || "")
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase()
                            .substring(0, 2);
                          cellData = (
                            <Flex align="center" gap={3}>
                              <Avatar
                                name={station.Name}
                                size="sm"
                                bg="#f57d36"
                                color="white"
                                fontWeight="bold"
                                getInitials={() => initials}
                              />
                              <Box>
                                <Text color={textColor} fontWeight="semibold" fontSize="sm">
                                  {station.Name || "—"}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  {station.StationId || "—"}
                                </Text>
                              </Box>
                            </Flex>
                          );
                        } else if (cell.column.Header === "Station ID") {
                          cellData = (
                            <Flex align="center" justify="center">
                              <Text color={textColor} fontSize="sm" fontFamily="mono">
                                {station.StationId || "—"}
                              </Text>
                            </Flex>
                          );
                        } else if (cell.column.Header === "Location ID") {
                          cellData = (
                            <Flex align="center" justify="center">
                              <Badge
                                bg="#f57d36"
                                color="white"
                                borderRadius="full"
                                px={3}
                                py={1}
                                fontWeight="bold"
                              >
                                {station.LocationId}
                              </Badge>
                            </Flex>
                          );
                        } else if (cell.column.Header === "OCPI Enabled") {
                          cellData = (
                            <Flex align="center" justify="center" gap={2}>
                              {canEdit && (
                                <Switch
                                  colorScheme="orange"
                                  isChecked={!!station.IsOcpiEnabled}
                                  isDisabled={updatingStationId === station.Id}
                                  onChange={(e) => handleToggle(station, e.target.checked)}
                                />
                              )}
                              {updatingStationId === station.Id ? (
                                <Spinner size="xs" color="#f57d36" />
                              ) : (
                                <Badge
                                  colorScheme={station.IsOcpiEnabled ? "green" : "gray"}
                                  borderRadius="full"
                                >
                                  {station.IsOcpiEnabled ? "Enabled" : "Disabled"}
                                </Badge>
                              )}
                            </Flex>
                          );
                        }
                        return (
                          <Td
                            {...cell.getCellProps()}
                            key={cellIdx}
                            py={4}
                            px={6}
                            borderColor={borderColor}
                            fontSize="sm"
                          >
                            {cellData}
                          </Td>
                        );
                      })}
                    </MotionTr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
          {renderPagination()}
        </>
      )}

      {/* Disable confirmation dialog */}
      <AlertDialog
        isOpen={confirmState.open}
        leastDestructiveRef={cancelRef}
        onClose={closeConfirm}
        isCentered
        motionPreset="slideInBottom"
      >
        <AlertDialogOverlay bg="rgba(0, 0, 0, 0.2)" backdropFilter="blur(12px)">
          <AlertDialogContent
            bg="rgba(255, 255, 255, 0.8)"
            backdropFilter="blur(20px)"
            borderRadius="16px"
            border="1px solid rgba(245, 125, 54, 0.4)"
            boxShadow="0 8px 32px rgba(245, 125, 54, 0.15)"
            p={3}
          >
            <AlertDialogHeader
              fontSize="xl"
              fontWeight="600"
              color="#333"
              pb={2}
              borderBottom="1px solid rgba(245, 125, 54, 0.2)"
            >
              <Flex align="center">
                <Icon as={MdLock} mr={2} color="#f57d36" boxSize={5} />
                Disable Station Roaming
              </Flex>
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb={3} fontSize="md">
                Disabling OCPI for station{" "}
                <strong>
                  {confirmState.station?.StationId || confirmState.station?.Id}
                </strong>{" "}
                will notify OCPI partners that all of its EVSEs are{" "}
                <strong>REMOVED</strong>. The station itself and its data
                remain unaffected internally.
              </Text>
              <Text fontWeight="500" textAlign="center">
                Do you want to proceed?
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter pt={3} borderTop="1px solid rgba(245, 125, 54, 0.2)">
              <Button
                ref={cancelRef}
                onClick={closeConfirm}
                variant="outline"
                borderColor="rgba(245, 125, 54, 0.5)"
                color="gray.600"
                borderRadius="8px"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                ml={3}
                bgGradient="linear(135deg, #f57d36 0%, #f35626 100%)"
                color="white"
                borderRadius="8px"
                fontWeight="600"
              >
                Disable
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </GlassMorphismCard>
  );
};

export default OcpiStationsTable;
